import { apiGet } from './apiClient';

export type AiAssistantAccessMode = 'full' | 'trial' | 'limited' | 'none';

export type AiAssistantAccessInfo = {
  hasAccess: boolean;
  mode: AiAssistantAccessMode;
  trialLimit: number;
  trialUsed: number;
  trialRemaining: number;
};

export type AiAssistantTrialInfo = {
  mode: AiAssistantAccessMode;
  trialLimit: number;
  trialUsed: number;
  trialRemaining: number;
};

export function isQuotaLimitedAiMode(mode?: AiAssistantAccessMode | string): boolean {
  return mode === 'trial' || mode === 'limited';
}

const DEFAULT_ACCESS: AiAssistantAccessInfo = {
  hasAccess: false,
  mode: 'none',
  trialLimit: 3,
  trialUsed: 0,
  trialRemaining: 0,
};

function normalizeAccessMode(value: unknown, hasAccess: boolean): AiAssistantAccessMode {
  if (value === 'full' || value === 'trial' || value === 'limited' || value === 'none') {
    return value;
  }
  return hasAccess ? 'full' : 'none';
}

export async function fetchAiAssistantAccess(): Promise<AiAssistantAccessInfo> {
  try {
    const payload = await apiGet<AiAssistantAccessInfo>('/ai-assistant/access');
    if (!payload) return DEFAULT_ACCESS;
    const hasAccess = Boolean(payload.hasAccess);
    return {
      hasAccess,
      mode: normalizeAccessMode(payload.mode, hasAccess),
      trialLimit: Number(payload.trialLimit) || 3,
      trialUsed: Number(payload.trialUsed) || 0,
      trialRemaining: Number(payload.trialRemaining) || 0,
    };
  } catch (error) {
    console.error('fetchAiAssistantAccess error:', error);
    return DEFAULT_ACCESS;
  }
}

/** @deprecated используйте fetchAiAssistantAccess */
export async function checkAiAssistantAccess(): Promise<boolean> {
  const access = await fetchAiAssistantAccess();
  return access.hasAccess;
}

export function mergeTrialInfo(
  access: AiAssistantAccessInfo,
  trial?: Partial<AiAssistantTrialInfo> | null,
): AiAssistantAccessInfo {
  if (!trial) return access;
  const trialLimit = Number(trial.trialLimit) || access.trialLimit;
  const trialUsed = Number(trial.trialUsed ?? access.trialUsed);
  const trialRemaining = Number(
    trial.trialRemaining ?? Math.max(0, trialLimit - trialUsed),
  );
  const incomingMode = normalizeAccessMode(trial.mode ?? access.mode, access.hasAccess);
  let mode: AiAssistantAccessMode = incomingMode;
  if (incomingMode === 'full') {
    mode = 'full';
  } else if (incomingMode === 'limited') {
    mode = trialRemaining > 0 ? 'limited' : 'none';
  } else if (incomingMode === 'trial') {
    mode = trialRemaining > 0 ? 'trial' : 'none';
  } else {
    mode = 'none';
  }
  return {
    hasAccess: mode === 'full' || (isQuotaLimitedAiMode(mode) && trialRemaining > 0),
    mode,
    trialLimit,
    trialUsed,
    trialRemaining,
  };
}
